import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './TrainerPage.css';

// Import trainer data from different sources
import { getTrainerData } from '../data/trainers';
import { getStrengthTrainerData } from '../data/trainers/StrengthTrainers';
import { getBandsTrainerData } from '../data/trainers/BandsTrainers';
import { getEverythingTrainerData } from '../data/trainers/EverythingTrainers';
import heroTrainersData from '../data/trainers/HeroTrainersData';

const TrainerPage = () => {
    const navigate = useNavigate();
    const { trainerId, programId } = useParams();
    const location = useLocation();

    // State for trainer and program selection
    const [trainer, setTrainer] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [selectedIntensity, setSelectedIntensity] = useState(100);
    const [showIntensityModal, setShowIntensityModal] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);

    // NEW: State for tracking completed programs
    const [completedPrograms, setCompletedPrograms] = useState({});

    // Determine trainer category from URL path
    const getTrainerCategory = () => {
        const path = location.pathname;
        if (path.includes('/strength-trainer/')) return 'strength';
        if (path.includes('/bands-trainer/')) return 'bands';
        if (path.includes('/everything-trainer/')) return 'everything';
        if (path.includes('/hero-trainers/') || location.state?.isHeroTrainer) return 'hero';
        return 'bodyweight'; // default
    };

    const trainerCategory = getTrainerCategory();

    // Get category-specific configuration
    const getCategoryConfig = (category) => {
        const configs = {
            strength: {
                theme: 'strength-theme',
                sounds: {
                    rep: 'strengthRep2',
                    start: 'strengthStart2',
                    set: 'strengthSet2'
                },
                backRoute: '/strength-trainers',
                workoutType: 'strength'
            },
            bands: {
                theme: 'bands-theme',
                sounds: {
                    rep: 'bandsRep2',
                    start: 'bandsStart2',
                    set: 'bandsSet2'
                },
                backRoute: '/bands-trainers',
                workoutType: 'bands'
            },
            everything: {
                theme: 'everything-theme',
                sounds: {
                    rep: 'everythingRep2',
                    start: 'everythingStart2',
                    set: 'everythingSet2'
                },
                backRoute: '/everything-trainers',
                workoutType: 'everything'
            },
            hero: {
                theme: 'hero-theme',
                sounds: {
                    rep: 'gameIntroMode2',
                    start: 'gameIntroMode2',
                    set: 'gameIntroMode2'
                },
                backRoute: '/hero-trainers',
                workoutType: 'hero'
            },
            bodyweight: {
                theme: 'bodyweight-theme',
                sounds: {
                    rep: 'bodyweightRep2',
                    start: 'bodyweightStart2',
                    set: 'bodyweightSet2'
                },
                backRoute: '/bodyweight-trainers',
                workoutType: 'bodyweight'
            }
        };
        return configs[category];
    };

    const categoryConfig = getCategoryConfig(trainerCategory);

    // NEW: Function to analyze saved workouts and track completed trainer programs
    const analyzeCompletedPrograms = () => {
        try {
            const savedWorkouts = JSON.parse(localStorage.getItem('savedWorkouts') || '[]');
            const completed = {};

            savedWorkouts.forEach(workout => {
                // Check if this is a trainer program workout
                if (workout.isTrainerProgram && workout.trainerName && workout.programName) {
                    // Create a unique key for this trainer + program combination
                    const key = `${workout.trainerName}-${workout.programName}`;

                    // Initialize or increment the count
                    if (completed[key]) {
                        completed[key].count += 1;
                        // Keep track of the most recent completion date
                        if (new Date(workout.date) > new Date(completed[key].lastCompleted)) {
                            completed[key].lastCompleted = workout.date;
                        }
                    } else {
                        completed[key] = {
                            trainerName: workout.trainerName,
                            programName: workout.programName,
                            count: 1,
                            lastCompleted: workout.date,
                            intensityLevels: [workout.intensityLevel || 100] // Track intensity levels used
                        };
                    }

                    // Add intensity level if not already tracked
                    if (!completed[key].intensityLevels.includes(workout.intensityLevel)) {
                        completed[key].intensityLevels.push(workout.intensityLevel || 100);
                    }
                }
            });

            setCompletedPrograms(completed);
            console.log('Completed programs analysis:', completed);
        } catch (error) {
            console.error('Error analyzing completed programs:', error);
            setCompletedPrograms({});
        }
    };

    // NEW: Function to get completion status for a specific program
    const getProgramCompletionStatus = (programName) => {
        if (!trainer) return { completed: false, count: 0 };

        const key = `${trainer.name}-${programName}`;
        const completion = completedPrograms[key];

        return {
            completed: !!completion,
            count: completion ? completion.count : 0,
            lastCompleted: completion ? completion.lastCompleted : null,
            intensityLevels: completion ? completion.intensityLevels : []
        };
    };

    // Load trainer data based on ID and category
    useEffect(() => {
        console.log(`Loading trainer for category: ${trainerCategory}, trainerId: ${trainerId}`);

        let trainerData = null;

        // Check if this is a hero trainer
        if (trainerCategory === 'hero' || location.state?.isHeroTrainer) {
            trainerData = heroTrainersData[trainerId];
            console.log(`Looking for hero trainer: ${trainerId}`);
        } else {
            // Look in regular trainers
            trainerData = getTrainerData(trainerId);
        }

        if (trainerData) {
            setTrainer(trainerData);
            console.log(`Found trainer data for ${trainerId}:`, trainerData);

            // If we have a programId in the URL, this means we're coming from hero trainer selection
            // and should show the intensity modal immediately
            if (programId && location.state?.programData) {
                setSelectedProgram(location.state.programData);
                setShowIntensityModal(true);
            }
        } else {
            console.log(`Trainer not found: ${trainerId}, redirecting to ${categoryConfig.backRoute}`);
            navigate(categoryConfig.backRoute);
        }

        // Initialize or get current round from localStorage
        const savedRound = localStorage.getItem('currentGameRound');
        if (savedRound) {
            setCurrentRound(parseInt(savedRound, 10));
        }
    }, [trainerId, programId, navigate, trainerCategory, categoryConfig.backRoute, location.state]);

    // NEW: Analyze completed programs when trainer loads or when component mounts
    useEffect(() => {
        analyzeCompletedPrograms();
    }, [trainer]);

    // Intensity level options (updated to include Game Mode)
    const intensityLevels = [
        {
            level: 100,
            label: 'Advanced',
            description: 'Full intensity - as designed by the trainer',
            color: '#FF0000',
            multiplier: 1.0
        },
        {
            level: 75,
            label: 'Intermediate',
            description: '75% intensity - slightly reduced volume',
            color: '#FF8C00',
            multiplier: 0.75
        },
        {
            level: 50,
            label: 'Beginner',
            description: '50% intensity - half the volume',
            color: '#FFD700',
            multiplier: 0.5
        },
        {
            level: 25,
            label: 'Recovery',
            description: '25% intensity - light recovery sessions',
            color: '#32CD32',
            multiplier: 0.25
        },
        {
            level: 'game',
            label: 'GAME MODE',
            description: `Progressive card game: Round ${currentRound} = ${currentRound} ${currentRound === 1 ? 'rep' : 'reps'} per exercise`,
            color: '#8A2BE2',
            multiplier: 'game',
            isGameMode: true
        }
    ];

    // Handle advancing to next round (for game mode)
    const handleNextRound = () => {
        const nextRound = currentRound + 1;
        setCurrentRound(nextRound);
        localStorage.setItem('currentGameRound', nextRound.toString());
        console.log(`🎮 Advanced to Round ${nextRound}`);
    };

    // Handle resetting game mode
    const handleResetGame = () => {
        setCurrentRound(1);
        localStorage.setItem('currentGameRound', '1');
        console.log('🎮 Game reset to Round 1');
    };

    // Handle program selection
    const handleProgramSelect = (program) => {
        soundManager.playSound(categoryConfig.sounds.rep, 0.6);
        setSelectedProgram(program);
    };

    // Handle start workout - show intensity modal
    const handleStartWorkout = () => {
        if (!selectedProgram) {
            alert('Please select a workout program first!');
            return;
        }

        const startSound = categoryConfig.sounds.start;
        soundManager.playSound(startSound, 0.7);
        setShowIntensityModal(true);
    };

    // Handle intensity selection and start workout
    const handleIntensitySelect = (intensity) => {
        soundManager.playSound('gameIntroMode2', 0.8);
        setSelectedIntensity(intensity.level);
        setShowIntensityModal(false);

        let adjustedReps, adjustedSets;
        let isGameMode = false;
        let gameRound = null;

        if (intensity.multiplier === 'game') {
            // GAME MODE: Progressive reps based on current round
            adjustedReps = currentRound; // Round 1 = 1 rep, Round 2 = 2 reps, etc.
            adjustedSets = 1; // Always 1 set in game mode
            isGameMode = true;
            gameRound = currentRound;
            console.log(`🎮 GAME MODE: Round ${currentRound} = ${adjustedReps} reps`);
        } else {
            adjustedReps = Math.max(1, Math.round(selectedProgram.reps * intensity.multiplier));
            adjustedSets = Math.max(1, Math.round(selectedProgram.sets * intensity.multiplier));
        }

        const workoutCode = selectedProgram.pattern;

        // Navigate to workout summary with all the necessary data
        navigate('/workout-summary', {
            state: {
                workoutCode: workoutCode,
                workoutType: categoryConfig.workoutType,
                repCount: adjustedReps,
                setCount: adjustedSets,
                difficultyLevel: 4,
                selectedMuscleGroups: workoutCode,
                isTrainerProgram: true,
                trainerName: trainer.name,
                programName: selectedProgram.name,
                programDescription: selectedProgram.description,
                programFocus: selectedProgram.focus,
                intensityLevel: isGameMode ? 100 : intensity.level,
                intensityLabel: isGameMode ? `Game Mode - Round ${currentRound}` : intensity.label,
                trainerCategory: trainerCategory,
                isGameMode: isGameMode,
                currentRound: gameRound
            }
        });
    };

    // Handle back navigation
    const handleBack = () => {
        soundManager.playButtonSound();

        // If we came from hero trainer selection, go back to that specific hero trainer
        if (trainerCategory === 'hero' && trainerId) {
            navigate(`/hero-trainers/${trainerId}`);
        } else {
            navigate(categoryConfig.backRoute);
        }
    };

    if (!trainer) {
        return (
            <div className="trainer-loading">
                <div className="loading-spinner"></div>
                <p>Loading trainer...</p>
            </div>
        );
    }

    return (
        <div className={`${categoryConfig.theme} trainer-page-container`}>
            {/* Trainer background image */}
            <div
                className="trainer-background"
                style={{
                    backgroundImage: `url(${trainer.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="trainer-overlay"></div>
            </div>

            {/* Back button */}
            <button className="back-button" onClick={handleBack}>
                ← Back to {trainerCategory === 'hero' ? 'Hero' : trainerCategory.charAt(0).toUpperCase() + trainerCategory.slice(1)} Trainers
            </button>

            {/* Trainer header */}
            <header className="trainer-header">
                <div className="trainer-photo">
                    <img src={trainer.image} alt={trainer.name} />
                    <div className="experience-badge">{trainer.experience}</div>
                </div>
                <div className="trainer-info">
                    <h1 className="trainer-name">{trainer.name}</h1>
                    <h2 className="trainer-specialization">{trainer.specialization}</h2>
                    <p className="trainer-intro">{trainer.intro}</p>
                </div>
            </header>

            {/* Programs section */}
            <section className="programs-section">
                <h3>Training Programs</h3>
                <p>Select a 6-week program designed by {trainer.name}</p>

                <div className="programs-grid">
                    {trainer.programs.map((program) => {
                        // NEW: Get completion status for this program
                        const completionStatus = getProgramCompletionStatus(program.name);

                        return (
                            <div
                                key={program.id}
                                className={`program-card ${selectedProgram?.id === program.id ? 'selected' : ''} ${completionStatus.completed ? 'completed' : ''}`}
                                onClick={() => handleProgramSelect(program)}
                            >
                                {/* NEW: Completion indicator */}
                                {completionStatus.completed && (
                                    <div className="completion-indicator">
                                        <span className="checkmark">✓</span>
                                        {completionStatus.count > 1 && (
                                            <span className="completion-count">{completionStatus.count}</span>
                                        )}
                                    </div>
                                )}

                                <div className="program-header">
                                    <h4 className="program-name">{program.name}</h4>
                                    <div className="program-stats">
                                        <span className="stat">{program.sets} sets</span>
                                        <span className="stat">{program.reps} reps</span>
                                    </div>
                                </div>
                                <p className="program-description">{program.description}</p>

                                {/* NEW: Show completion details if completed */}
                                {completionStatus.completed && (
                                    <div className="completion-details">
                                        <small>
                                            Completed {completionStatus.count} time{completionStatus.count > 1 ? 's' : ''}
                                            {completionStatus.lastCompleted && (
                                                <span> • Last: {new Date(completionStatus.lastCompleted).toLocaleDateString()}</span>
                                            )}
                                        </small>
                                    </div>
                                )}

                                <div className="program-features">
                                    <span className="feature">6 Week Program</span>
                                    <span className="feature">Progressive</span>
                                    {completionStatus.completed && (
                                        <span className="feature completed-feature">Completed</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Selected program details */}
            {selectedProgram && (
                <section className="selected-program-section">
                    <div className="program-details-card">
                        <h4>{selectedProgram.name} - Program Details</h4>
                        <div className="program-content">
                            <p><strong>Description:</strong> {selectedProgram.description}</p>
                            <p><strong>Default Settings:</strong> {selectedProgram.sets} sets × {selectedProgram.reps} reps</p>
                            <p><strong>Focus:</strong> {selectedProgram.focus || `Progressive ${trainerCategory} training`}</p>

                            {/* NEW: Show completion history for selected program */}
                            {(() => {
                                const status = getProgramCompletionStatus(selectedProgram.name);
                                if (status.completed) {
                                    return (
                                        <div className="selected-program-history">
                                            <p><strong>Your Progress:</strong> Completed {status.count} time{status.count > 1 ? 's' : ''}</p>
                                            {status.intensityLevels.length > 0 && (
                                                <p><strong>Intensity Levels Completed:</strong> {status.intensityLevels.sort((a, b) => b - a).join('%, ')}%</p>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <button
                            className="start-program-button"
                            onClick={handleStartWorkout}
                        >
                            <span className="button-icon">🚀</span>
                            <span className="button-text">
                                {getProgramCompletionStatus(selectedProgram.name).completed ? 'Restart This Program' : 'Start This Program'}
                            </span>
                        </button>
                    </div>
                </section>
            )}

            {/* Intensity selection modal */}
            {showIntensityModal && (
                <div className="intensity-modal-overlay" onClick={() => setShowIntensityModal(false)}>
                    <div className="intensity-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Choose Your Intensity Level</h3>
                        <p>Select the intensity that matches your current fitness level</p>

                        {/* Game Mode Round Display */}
                        <div className="game-mode-status-modal">
                            <div className="current-round-display">
                                <h4>🎮 Current Game Round: {currentRound}</h4>
                                <div className="round-controls">
                                    <button className="round-button-small" onClick={handleNextRound}>
                                        Advance to Round {currentRound + 1}
                                    </button>
                                    <button className="round-button-small reset" onClick={handleResetGame}>
                                        Reset to Round 1
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="intensity-options">
                            {intensityLevels.map((intensity) => {
                                // NEW: Show if user has completed this program at this intensity
                                const programStatus = getProgramCompletionStatus(selectedProgram.name);
                                const hasCompletedAtThisIntensity = intensity.isGameMode ?
                                    programStatus.intensityLevels.includes('game') :
                                    programStatus.intensityLevels.includes(intensity.level);

                                return (
                                    <div
                                        key={intensity.level}
                                        className={`intensity-option ${hasCompletedAtThisIntensity ? 'previously-completed' : ''} ${intensity.isGameMode ? 'game-mode-option' : ''}`}
                                        style={{ '--intensity-color': intensity.color }}
                                        onClick={() => handleIntensitySelect(intensity)}
                                    >
                                        {hasCompletedAtThisIntensity && (
                                            <div className="intensity-completed-indicator">✓</div>
                                        )}
                                        <div className="intensity-level">
                                            {intensity.isGameMode ? '✨' : `${intensity.level}%`}
                                        </div>
                                        <div className="intensity-label">{intensity.label}</div>
                                        <div className="intensity-description">{intensity.description}</div>
                                        <div className="intensity-preview">
                                            {intensity.isGameMode ?
                                                `${currentRound} reps × 1 set` :
                                                `${Math.round(selectedProgram.reps * intensity.multiplier)} reps × ${Math.round(selectedProgram.sets * intensity.multiplier)} sets`
                                            }
                                        </div>
                                        {intensity.isGameMode && (
                                            <div className="game-mode-preview-text">
                                                <small>Next round will be {currentRound + 1} reps per exercise</small>
                                            </div>
                                        )}
                                        {hasCompletedAtThisIntensity && (
                                            <div className="intensity-completed-text">Previously completed</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            className="close-modal-button"
                            onClick={() => setShowIntensityModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainerPage;